import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import Images from "assets/images"
import { IDLE, ASC } from "const"
import { deepCopy } from "client/utils"
import {
    StoreType,
    SortAccounts,
} from "client/store"
import {
    Logo,
    DeleteButton,
    SelectDropdownSearch,
    Empty,
} from "client/components"
import {
    AccountDropdownWrapper,
    AccountPreview,
    AccountName,
    AccountDropdownButton,
    AccountDropdownItem,
} from "./styled"


export interface AccountDropdownProps {
    selectedId: number
    title: string
    showDelete?: boolean
    onChange: (value: number) => void
}

export interface AccountDropdownListProps {
    selectedIds: readonly number[]
    title: string
    onChange: (value: number[]) => void
}

export const AccountDropdown = ({
    selectedId,
    title,
    showDelete = true,
    onChange,
}: AccountDropdownProps) => {
    const accountList = useSelector((state: StoreType) => state.account.accountList)
    const selectedAccount = accountList[selectedId]

    const [accountIds, setAccountIds] = useState<number[]>([])
    const [search, setSearch] = useState<string>("")

    useEffect(() => {
        const sortedIds = SortAccounts(Object.values(accountList), "name", ASC, { search })
        setAccountIds(sortedIds)
    }, [accountList, search])

    return (
        <AccountDropdownWrapper>
            {
                !selectedAccount && (
                    <AccountPreview>
                        <Empty />
                    </AccountPreview>
                )
            }
            {
                selectedAccount && (
                    <AccountPreview>
                        <Logo
                            logo={selectedAccount.logo || Images.accountLogo}
                            size={30}
                            borderRadius={4}
                        />
                        <AccountName>{selectedAccount.name}</AccountName>
                        {
                            showDelete && (
                                <DeleteButton
                                    status={IDLE}
                                    onClick={() => onChange(null)}
                                />
                            )
                        }
                    </AccountPreview>
                )
            }
            <AccountDropdownButton title={title}>
                <SelectDropdownSearch
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClearSearch={() => setSearch("")}
                />
                {
                    accountIds.map((accountId) => {
                        const account = accountList[accountId]
                        return accountId !== selectedId && account && (
                            <AccountDropdownItem
                                key={accountId}
                                onClick={() => onChange(accountId)}
                            >
                                <Logo
                                    logo={account.logo || Images.accountLogo}
                                    size={30}
                                    borderRadius={4}
                                />
                                <AccountName>{account.name}</AccountName>
                            </AccountDropdownItem>
                        )
                    })
                }
            </AccountDropdownButton>
        </AccountDropdownWrapper>
    )
}


export const AccountDropdownList = ({
    selectedIds,
    title,
    onChange,
}: AccountDropdownListProps) => {
    const accountList = useSelector((state: StoreType) => state.account.accountList)

    const [accountIds, setAccountIds] = useState<number[]>([])
    const [search, setSearch] = useState<string>("")

    useEffect(() => {
        const sortedIds = SortAccounts(Object.values(accountList), "name", ASC, { search })
        setAccountIds(sortedIds)
    }, [accountList, search])

    return (
        <AccountDropdownWrapper>
            {
                selectedIds.length === 0 && (
                    <AccountPreview>
                        <Empty />
                    </AccountPreview>
                )
            }
            {
                selectedIds.map((accountId) => {
                    const account = accountList[accountId]
                    return account && (
                        <AccountPreview key={accountId}>
                            <Logo
                                logo={account.logo || Images.accountLogo}
                                size={30}
                                borderRadius={4}
                            />
                            <AccountName>{account.name}</AccountName>
                            <DeleteButton
                                status={IDLE}
                                onClick={() => onChange(selectedIds.filter((id) => id !== accountId))}
                            />
                        </AccountPreview>
                    )
                })
            }
            <AccountDropdownButton title={title}>
                <SelectDropdownSearch
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClearSearch={() => setSearch("")}
                />
                {
                    accountIds.map((accountId) => {
                        const account = accountList[accountId]
                        const accountSelected = selectedIds && selectedIds.includes(accountId)
                        return !accountSelected && account && (
                            <AccountDropdownItem
                                key={accountId}
                                onClick={() => {
                                    const selectedIdsCopy: number[] = deepCopy(selectedIds)
                                    selectedIdsCopy.push(accountId)
                                    onChange(selectedIdsCopy)
                                }}
                            >
                                <Logo
                                    logo={account.logo || Images.accountLogo}
                                    size={30}
                                    borderRadius={4}
                                />
                                <AccountName>{account.name}</AccountName>
                            </AccountDropdownItem>
                        )
                    })
                }
            </AccountDropdownButton>
        </AccountDropdownWrapper>
    )
}
